// Modified PaymentOptions.jsx
import { useState, useEffect } from "react";
import { createSubscription } from "./subscriptionUtils";
import { apiCall } from '../../utils/api';
import RazorpayActivationNotice from "./RazorpayActivationNotice";
import PaymentLoadingOverlay from "./PaymentLoadingOverlay";

const PaymentOptions = ({
  user,
  plan,
  appId = "saleswit",
  onSuccess,
  onError,
}) => {
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState("");
  const [debug, setDebug] = useState({});
  const [showActivationNotice, setShowActivationNotice] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  useEffect(() => {
    // Add debug info to help troubleshoot
    let paymentGateways;
    try {
      paymentGateways = Array.isArray(plan.payment_gateways)
        ? plan.payment_gateways
        : typeof plan.payment_gateways === "string"
        ? JSON.parse(plan.payment_gateways)
        : ["razorpay"];
    } catch (err) {
      paymentGateways = ["razorpay"];
      console.error("Error parsing payment_gateways:", err);
    }

    const debugInfo = {
      plan_id: plan.id,
      plan_type: plan.plan_type || "unknown",
      paypal_plan_id: plan.paypal_plan_id || "missing",
      payment_gateways_raw: plan.payment_gateways,
      payment_gateways_parsed: paymentGateways,
      showRazorpay: paymentGateways.includes("razorpay"),
      showPayPal: paymentGateways.includes("paypal") && plan.paypal_plan_id,
    };

    setDebug(debugInfo);
  }, [plan]);

  // Handle return from payment and clear loading states
  useEffect(() => {
    // Clear loading overlay when component mounts (handles back button scenario)
    setShowLoadingOverlay(false);
    setSubscribing(false);

    // Check for additional payment completion
    const urlParams = new URLSearchParams(window.location.search);
    const additionalPayment = urlParams.get('additional_payment');
    
    if (additionalPayment === 'completed') {
      setShowActivationNotice(true);
      // Clear URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Check if we should show activation notice from localStorage
    if (localStorage.getItem('showRazorpayActivationNotice') === 'true') {
      setShowActivationNotice(true);
      localStorage.removeItem('showRazorpayActivationNotice');
    }
  }, []);

  // Handle page visibility change (when user returns from payment gateway)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // User returned to the page, clear loading states
        setShowLoadingOverlay(false);
        setSubscribing(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Parse the payment_gateways JSON array from the plan
  let paymentGateways = ["razorpay"]; // Default fallback
  try {
    paymentGateways = Array.isArray(plan.payment_gateways)
      ? plan.payment_gateways
      : typeof plan.payment_gateways === "string"
      ? JSON.parse(plan.payment_gateways)
      : ["razorpay"];
  } catch (err) {
    console.error("Error parsing payment_gateways:", err);
  }

  const showRazorpay = paymentGateways.includes("razorpay");
  const showPayPal = paymentGateways.includes("paypal") && plan.paypal_plan_id;

  const handleRazorpaySubscribe = async () => {
    if (!user) {
      setError("Please sign in to subscribe");
      return;
    }

    try {
      setSubscribing(true);
      setError("");
      setShowLoadingOverlay(true);

      // Use your existing subscription creation function
      const result = await createSubscription(
        user.uid,
        plan.razorpay_plan_id,
        appId
      );

      // If redirecting to Razorpay, clear loading state before redirect
      if (result && result.redirecting) {
        // Clear loading state immediately before redirect
        setShowLoadingOverlay(false);
        setSubscribing(false);
        
        // Store a flag in local storage to show activation notice after redirect back
        localStorage.setItem('showRazorpayActivationNotice', 'true');
        return;
      }

      // If it's a free plan or subscription was created successfully
      if (onSuccess && typeof onSuccess === "function") {
        onSuccess(result);
      }
      
      setShowLoadingOverlay(false);
    } catch (err) {
      setError("Unable to create subscription. Please verify your payment information and try again.");
      setShowLoadingOverlay(false);

      if (onError && typeof onError === "function") {
        onError(err);
      }
    } finally {
      setSubscribing(false);
    }
  };

  const handlePayPalSubscribe = async () => {
    if (!user) {
      setError("Please sign in to subscribe");
      return;
    }

    try {
      setSubscribing(true);
      setError("");
      setShowLoadingOverlay(true);

      // Call backend to create PayPal subscription
      const response = await apiCall('/api/subscriptions/create-paypal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.uid,
          plan_id: plan.paypal_plan_id,
          app_id: appId,
          customer_info: {
            email: user.email,
            first_name: user.displayName?.split(' ')[0] || 'User',
            last_name: user.displayName?.split(' ').slice(1).join(' ') || 'Name'
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create PayPal subscription');
      }

      const data = await response.json();
      
      if (data.result?.approval_url) {
        // Clear loading state before redirect to PayPal
        setShowLoadingOverlay(false);
        setSubscribing(false);
        
        // Store flag for when user returns
        localStorage.setItem('paypalPaymentInProgress', 'true');
        
        // Redirect to PayPal for approval
        window.location.href = data.result.approval_url;
        return;
      }

      // Success without approval needed (shouldn't happen for PayPal)
      if (onSuccess) {
        onSuccess(data.result);
      }
      
      setShowLoadingOverlay(false);

    } catch (err) {
      setError("Unable to process PayPal subscription. Please verify your PayPal account or try again later.");
      setShowLoadingOverlay(false);
      
      if (onError) {
        onError(err);
      }
    } finally {
      setSubscribing(false);
    }
  };

  // Don't render anything if no plan is provided
  if (!plan) {
    return null;
  }

  return (
    <div className="payment-options">
      {error && (
        <div className="error-message mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Razorpay Button */}
      {showRazorpay && (
        <div className="razorpay-option mb-4">
          <button
            onClick={handleRazorpaySubscribe}
            disabled={subscribing}
            className="w-full py-2 px-4 bg-[#3395FF] hover:bg-[#2A7FE6] text-white rounded-md font-medium flex items-center justify-center transition-colors duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
          >
            {subscribing ? "Processing..." : (
              <>
                <span>Subscribe with&nbsp;</span>
                <span className="italic">Razorpay</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* PayPal Button - Now uses backend API */}
      {showPayPal && (
        <div className="paypal-option mt-4">
          {showRazorpay && (
            <div className="text-center mb-2 text-gray-600 text-sm">
              -- OR --
            </div>
          )}
          <button
            onClick={handlePayPalSubscribe}
            disabled={subscribing}
            className="w-full py-2 px-4 bg-[#FFC439] hover:bg-[#FFB800] rounded-md font-semibold flex items-center justify-center transition-colors duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
          >
            {subscribing ? "Processing..." : (
              <>
                <span className="text-[#003087]">Subscribe with&nbsp;</span>
                <span className="text-[#003087] italic" style={{ transform: 'skew(-5deg, 0deg)' }}>Pay</span>
                <span className="text-[#009cde] italic" style={{ transform: 'skew(-5deg, 0deg)' }}>Pal</span>
              </>
            )}
          </button>
        </div>
      )}
      
      {showActivationNotice && (
        <RazorpayActivationNotice onClose={() => setShowActivationNotice(false)} />
      )}

      {/* Loading Overlay */}
      <PaymentLoadingOverlay 
        isVisible={showLoadingOverlay} 
        message="Processing Your Request..."
      />
    </div>
  );
};

export default PaymentOptions;